// Tiny inline-SVG icon set. Matches the Ionicons names the mobile
// app uses so the visual language is consistent across the two
// products. All icons paint with currentColor so they pick up the
// surrounding text colour without per-icon overrides.
//
// Usage:
//   <Icon name="flash" size={20} />
//   <Icon name="search" size={18} aria-label="Search" />

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round'
};

const FILL = { fill: 'currentColor' };

// Paths borrowed from the open-source Ionicons / Lucide vocabularies
// (MIT licensed) and adjusted for visual consistency.
const PATHS = {
  // Brand bolt — used for likes + battle accents.
  flash:           { d: 'M13 3 4 14h7l-1 7 9-11h-7l1-7Z', ...STROKE },
  'flash-filled':  { d: 'M13 3 4 14h7l-1 7 9-11h-7l1-7Z', ...FILL },
  home:            { d: 'M3 11 12 4l9 7v9a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1Z', ...STROKE },
  'home-filled':   { d: 'M3 11 12 4l9 7v9a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1Z', ...FILL },
  search:          { d: 'M11 4a7 7 0 1 1-4.95 11.95L3 19m13-8a5 5 0 1 0-10 0 5 5 0 0 0 10 0Z', ...STROKE },
  reels:           { d: 'M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Zm5 3 6 3-6 3Z', ...STROKE },
  battles:         { d: 'M14 5 9 11l3 3-6 6-2-2 6-6-3-3 6-5 4 4-3 3Zm2 11 4 4', ...STROKE },
  plus:            { d: 'M12 5v14M5 12h14', ...STROKE },
  bell:            { d: 'M6 8a6 6 0 0 1 12 0v5l1.5 3h-15L6 13Zm3 8a3 3 0 0 0 6 0', ...STROKE },
  chat:            { d: 'M4 7a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H9l-4 3v-3a3 3 0 0 1-1-2Z', ...STROKE },
  send:            { d: 'M4 12 20 4l-3 16-4-7-9-1Z', ...STROKE },
  bookmark:        { d: 'M6 4h12v17l-6-4-6 4Z', ...STROKE },
  'bookmark-filled': { d: 'M6 4h12v17l-6-4-6 4Z', ...FILL },
  repost:          { d: 'M4 9V7a2 2 0 0 1 2-2h11l-3-3m4 9v2a2 2 0 0 1-2 2H6l3 3m-5-6 3-3m6 9 3 3', ...STROKE },
  flag:            { d: 'M5 21V4h11l-2 4 2 4H5', ...STROKE },
  settings:        { d: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm9 4a8.9 8.9 0 0 0-.1-1.4l2.1-1.6-2-3.4-2.5.9a9 9 0 0 0-2.4-1.4L15.6 2h-4l-.5 3a9 9 0 0 0-2.4 1.4l-2.5-.9-2 3.4 2.1 1.6A8.9 8.9 0 0 0 6 12c0 .5 0 .9.1 1.4l-2.1 1.6 2 3.4 2.5-.9a9 9 0 0 0 2.4 1.4l.5 3h4l.5-3a9 9 0 0 0 2.4-1.4l2.5.9 2-3.4-2.1-1.6c.1-.5.1-.9.1-1.4Z', ...STROKE },
  trophy:          { d: 'M7 4h10v3a5 5 0 0 1-10 0Zm0 1H4a3 3 0 0 0 3 3m10-3h3a3 3 0 0 1-3 3m-7 7v3h4v-3M9 21h6', ...STROKE },
  history:         { d: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5m4-2v6l4 2', ...STROKE },
  shield:          { d: 'M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6Z', ...STROKE },
  'shield-check':  { d: 'M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6Zm-4 9 3 3 5-5', ...STROKE },
  signout:         { d: 'M9 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h3m4-4 4-3-4-3m4 3H9', ...STROKE },
  user:            { d: 'M12 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0', ...STROKE },
  arrowRight:      { d: 'M5 12h14m-5-6 6 6-6 6', ...STROKE },
  arrowLeft:       { d: 'M19 12H5m6 6-6-6 6-6', ...STROKE },
  close:           { d: 'M6 6 18 18M18 6 6 18', ...STROKE },
  more:            { d: 'M5 12h.01M12 12h.01M19 12h.01', ...STROKE, strokeWidth: 3 },
  check:           { d: 'm5 13 4 4L19 7', ...STROKE },
  edit:            { d: 'M4 20h4l11-11-4-4L4 16Zm9-13 4 4', ...STROKE },
  image:           { d: 'M3 5h18v14H3Zm0 11 6-6 5 5 3-3 4 4', ...STROKE },
  hashtag:         { d: 'M5 9h14M5 15h14M10 4 8 20M16 4l-2 16', ...STROKE },
  external:        { d: 'M11 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-6M13 11l8-8m0 0h-5m5 0v5', ...STROKE },
  location:        { d: 'M12 22s7-7 7-13a7 7 0 1 0-14 0c0 6 7 13 7 13Zm0-10a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z', ...STROKE }
};

export default function Icon({ name, size = 20, className = '', ariaLabel, style }) {
  const spec = PATHS[name];
  if (!spec) return null;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={{ flexShrink: 0, ...style }}
      role={ariaLabel ? 'img' : 'presentation'}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : 'true'}
      focusable="false"
    >
      <path {...spec} />
    </svg>
  );
}
